import { useEffect, useState } from "react";
import { Select, MenuItem, FormControl, InputLabel } from "@material-ui/core";
import DetailsDialog, { DetailsProps } from "../DetailsDialog";
import NumberInputField from "../ui/NumberInputField";
import TextInputField from "../ui/TextInputField";
import useSpringMaterialApi, { SpringMaterial } from "../../api/SpringMaterialApi";
import { SpringMaterialCustomerPrice } from "../../api/SpringMaterialCustomerPriceApi";
import { BigNumber } from "bignumber.js";

interface Props extends DetailsProps {
  readonly customerId: number;
  readonly priceListId: number;
  readonly materialPrices: SpringMaterialCustomerPrice[];
}

export interface BonnellSpring {
  id?: number;
  material: SpringMaterial;
  name: string;
  width: number;
  length: number;
}

const BonnellSpringDetails = (props: Props) => {
  const springMaterialApi = useSpringMaterialApi();
  const customerId = props.customerId;
  const materialPrices = props.materialPrices;
  const [materials, setMaterials] = useState<SpringMaterial[]>();
  const [userSelectedMaterial, setUserSelectedMaterial] = useState<SpringMaterial|null>(null);
  const [initialMaterialId, setInitialMaterialId] = useState<number>();
  const [name, setName] = useState<string>();
  const [width, setWidth] = useState<number>();
  const [length, setLength] = useState<number>();
  const [price, setPrice] = useState<BigNumber|null>();

  const initializeFields = (data: BonnellSpring) => {
    setUserSelectedMaterial(null);
    setInitialMaterialId(data.material.id);
    setName(data.name);
    setWidth(data.width);
    setLength(data.length);
  };

  const collectData = () =>
    (name && userSelectedMaterial && userSelectedMaterial.id && length && width)
    ? {
      name,
      material: userSelectedMaterial,
      width,
      length
    }
    : null;

  const fetchMaterials = () => {
    springMaterialApi
      .fetchBonnellSpringMaterials()
      .then(materials => setMaterials(materials))
      .catch(reason => console.error(`Could not fetch materials: ${reason}`));
  };

  const handleMaterialChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const selectedMaterial = event.target.value as SpringMaterial;
    setUserSelectedMaterial(selectedMaterial);
  };

  const isNotGreaterThanMinimalResolution = (value: BigNumber|null|undefined) =>
    value !== undefined && (value !== null && value.toNumber() < 0.01);

  const renderMaterialMenuItem = (material: SpringMaterial) => {
    const materialPrice = getMaterialPrice(material);
    return (
      <MenuItem key={material.id} value={material as any}>
        {material.name} ({material.thickness} mm) : {materialPrice === undefined ? '?' : materialPrice.unitPrice} zł
      </MenuItem>
    );
  };

  const getMaterialPrice = (material: SpringMaterial) => materialPrices[material.id];

  useEffect(() => {
    if (userSelectedMaterial && width !== undefined && length !== undefined) {
      const materialPrice = materialPrices[userSelectedMaterial.id];
      const total = materialPrice === undefined ? new BigNumber(0) : BigNumber(materialPrice.unitPrice).multipliedBy(width).multipliedBy(length);
      setPrice(total);
    }
  }, [materialPrices, userSelectedMaterial, width, length]);

  useEffect(() => {
    if (materials && initialMaterialId) {
      const selectedMaterial = materials.find(item => item.id === initialMaterialId);
      setUserSelectedMaterial(selectedMaterial || null);
    }
  }, [materials, initialMaterialId]);

  useEffect(() => {
    fetchMaterials();
  }, [customerId]);

  const renderFields = (initialData: BonnellSpring|undefined) => (
    <>
      <h2>Wprowadź dane bonnella</h2>
      {materials && <FormControl>
        <InputLabel>Materiał</InputLabel>
        <Select value={userSelectedMaterial || ''} onChange={handleMaterialChange}>
          { materials
            .filter(material => userSelectedMaterial === material || getMaterialPrice(material) !== undefined)
            .sort((material, anotherMaterial) => material.name.localeCompare(anotherMaterial.name))
            .map(renderMaterialMenuItem)
          }
        </Select>
      </FormControl>}
      <TextInputField
        label='Nazwa'
        autoFocus
        initialValue={initialData?.name}
        errorMessage={name => !name ? "Nazwa jest wymagana" : null}
        onValueChange={setName}
      />
      <NumberInputField
        label='Szerokość'
        initialValue={initialData?.width}
        onValueChange={setWidth}
      />
      <NumberInputField
        label='Długość'
        initialValue={initialData?.length}
        onValueChange={setLength}
      />
      <TextInputField
        label='Cena [zł]'
        calculatedValue={ price === undefined || price === null ? '?' : price.toFixed(2) }
        errorMessage={() => isNotGreaterThanMinimalResolution(price) ? "Wyliczona cena bazowa jest zbyt niska, wybierz inny materiał lub zwiększ wymiary" : null}
        disabled
      />
    </>
  );

  return <>
    <DetailsDialog<BonnellSpring>
      { ...props }
      resource={`customers/${customerId}/bonnell-springs`}
      renderFields={renderFields}
      initializeFields={initializeFields}
      collectData={collectData}
    />
  </>;
};
export default BonnellSpringDetails;